// apps/merchant-v3/src/lib/scope/useMeScope.ts
//
// Phase 6.1 Wave 5 Task 24 — FE store-scope reader. Consumes the BE
// GET /api/v1/auth/me/scope surface (Task 17, P6.1.D-21) so scope-aware
// pages can render the no-team empty state and hide KPI cards for members
// with no team assignment (P6.1.D-28b, G-12).
//
// Fail-closed semantics mirror the API contract (get-api-v1-me-scope.md):
//   - owner/admin  → isAllScope=true, allowedStoreIds enumerated, teamId=null
//   - team member  → isAllScope=false, allowedStoreIds = team stores, teamId set
//   - no-team/error → allowedStoreIds=[], isAllScope=false (empty means literally
//     zero — NEVER "all"). The resolver never 5xx-leaks; on any read error we
//     default to an empty, fail-closed scope so the FE never fails open.
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-27
// vg-binding: P6.1.D-28
// vg-binding: P6.1.D-02
// vg-binding: G-12
// vg-binding: G-15
// vg-binding: G-36
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, ApiError } from '@/lib/api/client';

/** Caller store scope — mirrors the inner `data` of GET /auth/me/scope. */
export interface MeScope {
  /** Fully enumerated allowed store ids — never `[]` as a sentinel for "all". */
  allowedStoreIds: string[];
  /** null = no-team member (sees zero). */
  teamId: string | null;
  /** true for owner/admin (bypass) — still enumerated. */
  isAllScope: boolean;
  /** Monotonic — bumps on any assignment change (D-21). */
  scopeVersion: number;
}

/** Stable React Query cache key for the caller scope. */
export const ME_SCOPE_QUERY_KEY = ['auth', 'me', 'scope'] as const;

const SCOPE_STALE_MS = 30_000;

/** Fail-closed empty scope — used as the default before/around any error. */
const EMPTY_SCOPE: MeScope = {
  allowedStoreIds: [],
  teamId: null,
  isAllScope: false,
  scopeVersion: 0,
};

function normalizeScope(raw: unknown): MeScope {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SCOPE };
  const r = raw as Record<string, unknown>;
  const ids = Array.isArray(r.allowedStoreIds)
    ? r.allowedStoreIds.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    allowedStoreIds: ids,
    teamId: typeof r.teamId === 'string' ? r.teamId : null,
    isAllScope: r.isAllScope === true,
    scopeVersion:
      typeof r.scopeVersion === 'number' && Number.isFinite(r.scopeVersion)
        ? r.scopeVersion
        : 0,
  };
}

async function fetchMeScope(): Promise<MeScope> {
  try {
    const raw = await apiClient.get<unknown>('/api/v1/auth/me/scope');
    return normalizeScope(raw);
  } catch (err) {
    // Fail-closed: on 403/404/network the caller resolves to an empty scope
    // (never fail-open to "all"). A genuine 401 is re-thrown so RequireAuth
    // can bounce to /login instead of masking the auth loss.
    if (err instanceof ApiError && err.status === 401) throw err;
    return { ...EMPTY_SCOPE };
  }
}

export interface UseMeScopeResult {
  scope: MeScope | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * true only when the response is DEFINITIVELY a no-team member:
   * isAllScope=false AND allowedStoreIds empty AND teamId null. Owner/admin
   * (isAllScope=true) and team members (allowedStoreIds populated / teamId set)
   * are never flagged. Undefined while loading (returns false).
   */
  isNoTeamMember: boolean;
}

/**
 * Read the caller's store scope. Drives the no-team empty state + KPI hide
 * on dashboard/orders/products (P6.1.D-28b, G-12) and the settings role
 * derivation. Result is cached 30s and shared across every consumer.
 */
export function useMeScope(): UseMeScopeResult {
  const query: UseQueryResult<MeScope, ApiError> = useQuery<MeScope, ApiError>({
    queryKey: ME_SCOPE_QUERY_KEY,
    queryFn: fetchMeScope,
    staleTime: SCOPE_STALE_MS,
    gcTime: 5 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
  });

  const scope = query.data ?? null;
  const isNoTeamMember =
    scope != null &&
    !scope.isAllScope &&
    scope.allowedStoreIds.length === 0 &&
    scope.teamId === null;

  return {
    scope,
    isLoading: query.isLoading,
    isError: query.isError,
    isNoTeamMember,
  };
}
